import { gql } from "generated-graphql";

/**
 * The document itself, for the review dialog's title bar and preview pane.
 * Deliberately narrow — `useDocumentMetadata` (the manifest review dialog's
 * hook) also runs a facility-match lookup this surface has no use for, since
 * decision 4 always shows a facility picker rather than suggesting one.
 */
export const SHIPPING_DOCUMENT_FILE = gql(`
  query ShippingDocumentFile($id: ID!) {
    document(id: $id) {
      id
      tenantId
      title
      fileExtension
      downloadLink
      extractionStatus
      extractionError
    }
  }
`);

/**
 * The AI reading of a bill of lading document, for the review grid.
 *
 * No polling here: the review dialog only opens once
 * `DocumentAIAnalyzingDialog` has already reported the enrichment job
 * SUCCEEDED, so by the time this query runs the candidate is already
 * written. `onRetry` (grid README) re-queues via `EXTRACT_BILL_OF_LADING`
 * and the caller re-fetches.
 */
export const EXTRACTED_BILL_OF_LADING = gql(`
  query ExtractedBillOfLading($documentId: ID!) {
    extractedBillOfLading(documentId: $documentId) {
      documentId
      candidateStatus
      error
      values {
        documentNumber
        generatorName
        transporterName
        destinationName
        shipDate
      }
      lines {
        description
        quantity
        unit
        declaredWasteKind
        federalWasteCodes
        stateWasteCodes
      }
      # What the model itself read, before any correction a reviewer saved. The
      # grid shows it as "Suggested: x" beside a field that no longer matches
      # and offers it back on click. Without it the grid's extracted baseline
      # falls back to values, which the resolver has already run corrections
      # over, so a second review pass shows a reviewer their own earlier edit
      # as the extraction and the model's reading is gone from the screen.
      asExtractedValues {
        documentNumber
        generatorName
        transporterName
        destinationName
        shipDate
      }
      asExtractedLines {
        description
        quantity
        unit
        declaredWasteKind
        federalWasteCodes
        stateWasteCodes
      }
      fieldConfidences {
        fieldPath
        confidence
      }
    }
  }
`);

/**
 * Queue Document-AI extraction of an uploaded bill of lading. Returns the
 * enrichment job id, which `DocumentAIAnalyzingDialog` polls the same way the
 * manifest upload flow does — this extractor rides the same job queue.
 */
export const EXTRACT_BILL_OF_LADING = gql(`
  mutation ExtractBillOfLading($documentId: ID!) {
    extractBillOfLading(documentId: $documentId)
  }
`);

/**
 * Record the reviewer's corrections against the candidate and write the
 * reviewed values to a `WasteShippingDocument`. Refetches the reading
 * alongside — an applied candidate flips to APPLIED, and leaving the old
 * reading cached would let a second open of the dialog offer to re-apply it.
 */
export const APPLY_EXTRACTED_BILL_OF_LADING = gql(`
  mutation ApplyExtractedBillOfLading($input: ApplyExtractedBillOfLadingInput!) {
    applyExtractedBillOfLading(input: $input) {
      id
    }
  }
`);

/** Row shape for the detail page's lines grid. */
gql(`
  fragment ShippingDocumentLineRow on ShippingDocumentLine {
    id
    description
    quantity
    unit
    declaredWasteKind
    federalWasteCodes
    stateWasteCodes
    wasteStream {
      id
      name
    }
    container {
      id
      containerLabel
    }
  }
`);

/**
 * One page of a document's lines. Separate from the detail read so the grid can
 * search, page and sort without refetching the document around it.
 */
export const SHIPPING_DOCUMENT_LINES_QUERY = gql(`
  query ShippingDocumentLines(
    $id: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    wasteShippingDocument(id: $id) {
      id
      linesResult(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          ...ShippingDocumentLineRow
        }
        count
      }
    }
  }
`);

/** Full detail read: header, facility, source document, and ordered lines with their matches. */
export const GET_SHIPPING_DOCUMENT_QUERY = gql(`
  query GetShippingDocument($id: ID!) {
    wasteShippingDocument(id: $id) {
      id
      kind
      documentNumber
      generatorName
      transporterName
      destinationName
      shipDate
      createdAt
      facility {
        id
        name
      }
      sourceDocument {
        id
        title
        fileExtension
        downloadLink
      }
      lines {
        id
        description
        quantity
        unit
        declaredWasteKind
        federalWasteCodes
        stateWasteCodes
        wasteStreamId
        containerId
        wasteStream {
          id
          name
        }
        container {
          id
          containerLabel
        }
      }
    }
  }
`);

/**
 * Wholesale-replace save (see `WasteShippingDocumentInput.lines` doc comment):
 * the edit dialog always sends the full line set, never a partial diff.
 */
export const SAVE_WASTE_SHIPPING_DOCUMENT = gql(`
  mutation SaveWasteShippingDocument($input: WasteShippingDocumentInput!) {
    saveWasteShippingDocument(input: $input) {
      id
    }
  }
`);

export const DELETE_WASTE_SHIPPING_DOCUMENT = gql(`
  mutation DeleteWasteShippingDocument($id: ID!) {
    deleteWasteShippingDocument(id: $id)
  }
`);
